import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { CreditCard } from 'lucide-react';
import { formatDate } from '@/lib/utils';

interface SubRow {
  id: string;
  tenantId: string;
  status: string;
  currency: string;
  billingPeriod: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  stripeSubscriptionId: string | null;
  plan: { code: string; name: string };
  tenant: { id: string; name: string; slug: string } | null;
}

export function PlatformSubscriptionsPage() {
  const subs = useQuery({
    queryKey: ['platform-admin', 'subscriptions'],
    queryFn: async () => (await api.get('/api/platform-admin/subscriptions')).data,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Subscriptions</h1>
        <p className="text-sm text-muted-foreground">All Stripe subscriptions across all tenants.</p>
      </div>

      {subs.isLoading ? (
        <Skeleton className="h-64" />
      ) : !subs.data?.items?.length ? (
        <EmptyState icon={CreditCard} title="No subscriptions" description="No tenants have subscribed yet." />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {subs.data.items.map((s: SubRow) => (
                <div key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {s.tenant ? (
                        <Link to={`/platform-admin/tenants/${s.tenant.id}`} className="font-medium hover:text-primary">
                          {s.tenant.name}
                        </Link>
                      ) : (
                        <span className="font-medium">(tenant deleted)</span>
                      )}
                      <Badge variant="secondary">{s.plan.name}</Badge>
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {s.billingPeriod} · {s.currency} ?{' '}
                      {formatDate(s.currentPeriodStart)} to {formatDate(s.currentPeriodEnd)}
                      {s.cancelAtPeriodEnd && ' ? ?? cancelling at period end'}
                    </div>
                  </div>
                  <Badge
                    variant={
                      s.status === 'active' || s.status === 'trialing'
                        ? 'success'
                        : s.status === 'past_due' || s.status === 'incomplete'
                          ? 'destructive'
                          : 'secondary'
                    }
                  >
                    {s.status}
                  </Badge>
                  {s.stripeSubscriptionId && (
                    <code className="text-[10px] text-muted-foreground">{s.stripeSubscriptionId}</code>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
